import { Alert, Box, Typography, useTheme } from '@mui/material';
import ExoForm from '../../components/exo/ExoForm';
import * as Yup from 'yup';
import { API_POST, DBRequest, useConfig } from '../../api/api';
import Feedback from '../../components/special/Feedback';
import { useEffect, useState } from 'react';
import moment from 'moment';
import { tokens } from '../../global/theme/tokens';
import { renderHours } from '../time-tracking/worktime';
import { useTranslation } from 'react-i18next';
import { useTimeTracking } from '../../components/time-tracking/AppTimeProvider';
import StartIcon from '@mui/icons-material/Start';
import KeyboardTabIcon from '@mui/icons-material/KeyboardTab';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import ContrastIcon from '@mui/icons-material/Contrast';
import WeekendIcon from '@mui/icons-material/Weekend';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { TimeAccountCard } from '../time-tracking/TimeAccountCard';
import useFetch from '../../api/useFetch';
import { TimeLabelElement } from '../time-manager/user-entries/timeline/TimeInfo';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import { useDebounce } from 'use-debounce';

const NewTimeCompensation = ({ onCreate, onCancel, minutesPerDay }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const config = useConfig();

  const { minutesPerWeek, payType, timeAccount } = useTimeTracking();

  const fields = [
    {
      Icon: StartIcon,
      label: t('Start'),
      type: 'date',
      key: 'startDate',
    },
    {
      Icon: KeyboardTabIcon,
      label: t('End'),
      type: 'date',
      key: 'endDate',
    },
    {
      label: t('Is half start date'),
      type: 'checkbox',
      key: 'halfStartDate',
    },
    {
      label: t('Is half end date'),
      type: 'checkbox',
      key: 'halfEndDate',
    },
    {
      label: t('Comment'),
      type: 'text',
      key: 'comment',
      multiline: true,
      colspan: 2,
    },
  ];

  const validationSchema = Yup.object().shape({
    startDate: Yup.string().required(t('Is required')),
    endDate: Yup.string().required(t('Is required')),
  });

  function handleSubmit(values) {
    const data = {
      type: 'timeCompensation',
      comment: values.comment,
      values: JSON.stringify({
        ...values,
        minutesPerWeek,
        daysFree: workdays.data.times,
        timeCost: workdays.timeCost,
      }),
    };
    DBRequest({
      config,
      path: 'employeerequests',
      method: API_POST(data),
      onResponse: handleCreation,
      onFeedback: handleAlertOpen,
    });
  }

  function handleCreation(data) {
    onCreate(data.data);
    onCancel();
  }

  // live calculation
  const [diff, setDiff] = useState(0);
  const [workdays, setWorkdays] = useState(null);
  const [dataToDebounce, setDataToDebounce] = useState({});
  const [debouncedData] = useDebounce(dataToDebounce, 700);
  const [error, setError] = useState(null);

  function handleChange(data) {
    const { startDate, endDate, halfStartDate, halfEndDate } = data;
    if (
      (!workdays ||
        startDate !== workdays.startDate ||
        endDate !== workdays.endDate ||
        halfStartDate !== workdays.halfStartDate ||
        halfEndDate !== workdays.halfEndDate) &&
      startDate &&
      endDate
    ) {
      setDataToDebounce(data);
    }
  }

  useEffect(() => {
    if (debouncedData) {
      const { startDate, endDate, halfStartDate, halfEndDate } = debouncedData;
      // validate dates
      const start = moment(startDate);
      const end = moment(endDate);
      if (start.isAfter(end))
        return setError('Start date must be before end date');

      if (end.diff(start, 'days') > 365)
        return setError('Vacation period cannot be longer than 365 days');

      setError(null);
      requestWorkdays(
        startDate,
        endDate,
        parseInt(halfStartDate),
        parseInt(halfEndDate)
      );
    }
  }, [debouncedData]);

  function requestWorkdays(startDate, endDate, halfStartDate, halfEndDate) {
    const formattedStart = moment(startDate).format('YYYY-MM-DD');
    const formattedEnd = moment(endDate).format('YYYY-MM-DD');

    DBRequest({
      config,
      path: `time-tracking/overview/own?period=day&startDate=${formattedStart}&endDate=${formattedEnd}`,
      onResponse: (data) =>
        handleWorkdaysResponse({
          ...data,
          startDate: formattedStart,
          endDate: formattedEnd,
          halfStartDate,
          halfEndDate,
        }),
    });
  }

  function handleWorkdaysResponse(data) {
    const { halfStartDate, halfEndDate } = data;
    var newTimes = data.data.times;

    var newDiff = 0;

    newTimes.forEach((time, index) => {
      const { totalTimeSeconds, targetTimeSeconds, percentages } = time;
      // startdate and enddate
      var { vacation, sick, holiday, overtime, worked } = percentages;

      const deviation = targetTimeSeconds - totalTimeSeconds;
      overtime = 0; // reset overtime

      if (deviation !== 0) {
        if (
          (halfStartDate && index === 0) ||
          (halfEndDate && index === newTimes.length - 1)
        ) {
          time.percentages.overtime = 0.5;
        } else {
          time.percentages.overtime = 1;
        }
        // set the isNew flag to true
        time.isNew = true;
      }

      if (time.percentages.overtime)
        newDiff += targetTimeSeconds * time.percentages.overtime;
    });
    setDiff((newDiff / 60) * -1); // convert to minutes and invert
    setWorkdays({
      ...data,
      data: { ...data.data, times: newTimes },
      timeCost: (newDiff / 60) * -1,
    });
  }

  // feedback
  const [alertState, setAlertState] = useState({
    alertOpen: false,
    alertType: 'info',
    alertText: 'test',
  });
  function handleAlertOpen(text, type) {
    setAlertState({ alertOpen: true, alertText: text, alertType: type });
  }

  function countDaysWithTargetMinutes(days) {
    if (!days) return 0;
    return days.filter((day) => day.targetTimeSeconds > 0).length;
  }

  return (
    <Box className="flex flex-col lg:flex-row h-full">
      <Box className="p-4 flex flex-col gap-2 w-full lg:max-w-[600px] lg:h-full">
        <Typography variant="h3" textAlign="center">
          {t('New Compensation')}
        </Typography>
        <Box className="flex justify-center items-center mb-2 gap-2">
          <Box className="p-4">
            <Typography variant="h3">{renderHours(diff, true)} h</Typography>
            <Typography variant="p">{t('time costs')}</Typography>
          </Box>
          {payType === 1 && (
            <>
              <ArrowForwardIcon />
              <TimeAccountCard
                size="small"
                minutes={timeAccount + diff}
                label={t('Time Account')}
              />
            </>
          )}
        </Box>
        {error && (
          <Box
            className="flex w-full items-center justify-center"
            sx={{
              '.MuiAlert-root': {
                flexDirection: 'row',
              },
            }}
          >
            <Alert severity={'error'} sx={{ width: '100%' }}>
              {error}
            </Alert>
          </Box>
        )}
        <ExoForm
          fields={fields}
          onSubmit={handleSubmit}
          onCancel={onCancel}
          validationSchema={validationSchema}
          onChange={handleChange}
          className="grid grid-cols-2 gap-4 pt-2"
          outerClassName="lg:h-full justify-between flex flex-col overflow-y-auto"
        />
        <Feedback setState={setAlertState} state={alertState} />
      </Box>
      <Box className="flex flex-col gap-2 py-2 lg:h-full overflow-y-auto w-full">
        <Box
          sx={{ bgcolor: colors.card }}
          className="backdrop-blur-md px-2 py-2 rounded-lg sticky top-0 flex justify-around z-10"
        >
          <Box>
            <Typography fontSize={12} fontWeight={600} className=" opacity-50">
              {t('Total')}
            </Typography>
            <Typography className="monospace">{`${
              workdays?.data.times.length || 0
            } ${
              workdays?.data.times.length > 1 ? t('Days') : t('Day')
            }`}</Typography>
          </Box>
          <Box>
            <Typography fontSize={12} fontWeight={600} className=" opacity-50">
              {t('Workdays')}
            </Typography>
            <Typography className="monospace">{`${countDaysWithTargetMinutes(
              workdays?.data?.times
            )} ${
              countDaysWithTargetMinutes(workdays?.data?.times) > 1
                ? t('Days')
                : t('Day')
            }`}</Typography>
          </Box>
        </Box>
        {workdays?.data?.times.map((day) => (
          <FreeDay day={day} />
        ))}
      </Box>
    </Box>
  );
};

export const FreeDay = ({ day }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  const { date, targetTimeSeconds, percentages, totalTimeSeconds, isNew } = day;
  const dayLabel = moment(date).format('ddd');
  const dayNumber = moment(date).format('DD');

  let { worked, vacation, sick, holiday, overtime } = percentages || {};
  if (!overtime) overtime = 0;

  const isDefaultFreeDay =
    worked === 0 &&
    vacation === 0 &&
    sick === 0 &&
    holiday === 0 &&
    targetTimeSeconds === 0;

  const correctionFactorExisting = overtime
    ? totalTimeSeconds / (targetTimeSeconds + totalTimeSeconds)
    : 1 || 0;

  const correctionFactorCompensation = overtime
    ? targetTimeSeconds / (targetTimeSeconds + totalTimeSeconds)
    : 1 || 0;

  const totalTimeLeft =
    (targetTimeSeconds + totalTimeSeconds) *
    (1 -
      (vacation + holiday + sick + worked) * correctionFactorExisting -
      overtime * correctionFactorCompensation);

  const isHalfDayVacation = totalTimeLeft !== 0;

  const color =
    worked === 1
      ? colors.grey[400]
      : isDefaultFreeDay
      ? colors.grey[400] // always free day
      : isHalfDayVacation
      ? colors.warning // half day vacation
      : overtime
      ? colors.success // overtime vacation
      : colors.info; // vacation

  const label =
    worked === 1
      ? t('Worked')
      : isDefaultFreeDay
      ? t('Always free')
      : isHalfDayVacation
      ? t('Half-Day Vacation')
      : overtime
      ? t('Overtime-Vacation')
      : t('Vacation');

  return (
    <Box className="flex gap-4 w-full">
      <Box className="relative">
        {isNew && (
          <Typography
            sx={{ bgcolor: colors.warning }}
            className=" absolute -top-2 right-0 translate-x-1/2 z-10 px-[5px] py-[2px] rounded-md rotate-12"
            fontSize={12}
            fontWeight={600}
          >
            {t('New')}
          </Typography>
        )}
        <Box
          sx={{ bgcolor: colors.card, height: '60px', minWidth: '60px' }}
          className="flex flex-col items-center justify-center rounded-lg gap-1 overflow-hidden relative pt-[5px]"
        >
          <Box
            sx={{ bgcolor: color }}
            className=" h-[10px] w-full absolute top-0 left-0"
          />
          <Typography
            fontWeight={600}
            fontSize="25px"
            lineHeight="25px"
            className="w-full opacity-80 monospace"
            textAlign="center"
          >
            {dayNumber}
          </Typography>
          <Typography
            fontSize="12px"
            className="w-full opacity-60"
            lineHeight="12px"
            textAlign="center"
            fontWeight={500}
          >
            {t(dayLabel)}
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{ bgcolor: colors.card }}
        className="w-full pl-3 pr-5 pb-2 pt-1 gap-4 flex flex-col md:flex-row lg:flex-col xl:flex-row items-center rounded-lg justify-between"
      >
        <Box className="flex flex-col ">
          <Typography
            fontSize="18px"
            className=" flex items-center"
            fontWeight={500}
            sx={{ color }}
          >
            {worked === 1 ? (
              <WorkOutlineIcon className="mr-2" fontSize="13px" />
            ) : !isHalfDayVacation ? (
              !isDefaultFreeDay ? (
                <BeachAccessIcon className="mr-2" fontSize="13px" />
              ) : (
                <WeekendIcon className="mr-2" fontSize="13px" />
              )
            ) : (
              <ContrastIcon className="mr-2" fontSize="13px" />
            )}
            {label}
          </Typography>
          {!isDefaultFreeDay && (
            <Box className="flex justify-evenly flex-wrap">
              {worked === 1 ? (
                <TimeLabelElement
                  src={'/assets/uploads/times/worked.webp'}
                  label={t('Worked')}
                  totalTime={targetTimeSeconds + totalTimeSeconds}
                  percent={worked * correctionFactorExisting}
                />
              ) : (
                <TimeLabelElement
                  src={'/assets/uploads/times/worked.webp'}
                  label={t('Time Compensation')}
                  totalTime={targetTimeSeconds + totalTimeSeconds}
                  percent={
                    overtime ? overtime * correctionFactorCompensation : 0
                  }
                />
              )}
              <TimeLabelElement
                src={'/assets/uploads/times/vacation.webp'}
                label={t('Vacation')}
                totalTime={targetTimeSeconds + totalTimeSeconds}
                percent={vacation * correctionFactorExisting}
              />
              <TimeLabelElement
                src={'/assets/uploads/times/holiday.webp'}
                label={t('Holiday')}
                totalTime={targetTimeSeconds + totalTimeSeconds}
                percent={holiday * correctionFactorExisting}
              />
            </Box>
          )}
        </Box>
        {totalTimeLeft > 0 && (
          <Box
            sx={{
              bgcolor: totalTimeLeft <= 0 ? 'transparent' : colors.warning,
            }}
            className="flex items-center justify-center px-3 py-1 flex-col rounded-lg"
          >
            <Box className="flex flex-col items-start justify-start">
              <Typography className="monospace" fontWeight={600} fontSize={18}>
                {renderHours(totalTimeLeft / 60)} h
              </Typography>
              <Typography fontSize={12}>{t('remaining')}</Typography>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default NewTimeCompensation;
